/**
 * Maps GET /api/verifast/logs/:id into UI session groups for View Verifast Logs.
 */

import type { VerifastIdentityLogRow, VerifastSessionGroup } from "../types/utils";
import {
  hasVerifastChecks,
  parseVerifastChecksSnapshot,
  type VerifastChecksSnapshot,
} from "./verifastChecksMapper";

export type { VerifastIdentityLogRow, VerifastSessionGroup } from "../types/utils";

const SMALL_WORDS = new Set([
  "and",
  "or",
  "the",
  "of",
  "in",
  "for",
  "to",
  "a",
  "an",
  "at",
  "by",
  "as",
]);

function titleCaseWords(words: string[]): string {
  return words
    .map((word, i) => {
      const lw = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lw)) return lw;
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join(" ");
}

function formatStatusLabel(status: string): string {
  const raw = status.trim();
  if (!raw) return "—";
  return titleCaseWords(raw.replace(/[_\s]+/g, " ").split(/\s+/).filter(Boolean));
}

function formatAtLabel(at: unknown): string {
  if (at == null || at === "") return "—";
  const text = String(at).trim();
  if (!text) return "—";
  const parsed = Date.parse(text);
  if (Number.isFinite(parsed)) {
    try {
      return new Date(parsed).toLocaleString();
    } catch {
      return text;
    }
  }
  const n = Number(at);
  if (Number.isFinite(n) && n > 0) {
    const ms = n > 1e12 ? n : n * 1000;
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return "—";
    }
  }
  return text;
}

const CHECKS_EVENT_NAMES = new Set([
  "verification.identity.completed",
  "session.completed",
  "session.approved",
  "session.conditionally_approved",
]);

function stepToRow(
  step: unknown,
  sessionChecks?: VerifastChecksSnapshot | null,
): VerifastIdentityLogRow {
  const row =
    typeof step === "object" && step !== null ? (step as Record<string, unknown>) : {};
  const statusRaw = row.status != null ? String(row.status) : "";
  const stepRaw = row.step != null ? String(row.step) : "";
  const eventRaw = row.event != null ? String(row.event) : "";
  const eventKey = eventRaw.toLowerCase();
  const checks =
    CHECKS_EVENT_NAMES.has(eventKey) && hasVerifastChecks(sessionChecks)
      ? sessionChecks
      : null;
  return {
    status: statusRaw ? formatStatusLabel(statusRaw) : "—",
    step: stepRaw || eventRaw || "—",
    event: eventRaw || "—",
    dateTimeLabel: formatAtLabel(row.at),
    checks,
    checksAvailable: hasVerifastChecks(checks),
  };
}

export type VerifastSummaryBadgeVariant = "complete" | "failed" | "neutral";

export function verifastSummaryStatusBadgeVariant(
  statusKey: string,
): VerifastSummaryBadgeVariant {
  const k = (statusKey || "").trim().toLowerCase();
  if (k === "complete" || k === "completed" || k === "approved") return "complete";
  if (k === "failed" || k === "rejected" || k === "expired") return "failed";
  if (k === "needs_review" || k === "awaiting_review" || k === "pending_review") {
    return "neutral";
  }
  return "neutral";
}

/** One group per Verifast session, newest first. */
export function buildVerifastSessionGroups(raw: unknown): VerifastSessionGroup[] {
  if (!Array.isArray(raw)) return [];

  const paired: { createdAt: number; group: VerifastSessionGroup }[] = [];
  for (const session of raw) {
    if (!session || typeof session !== "object") continue;
    const row = session as Record<string, unknown>;
    const sessionId =
      row.verifast_session_id != null
        ? String(row.verifast_session_id).trim()
        : "";
    const steps = Array.isArray(row.steps_json) ? row.steps_json : [];
    const verificationChecks = parseVerifastChecksSnapshot(row.verification_checks);
    const eventRows = steps.map((step) => stepToRow(step, verificationChecks));
    const statusRaw =
      row.id_verify_status != null
        ? String(row.id_verify_status)
        : row.completion_status != null
          ? String(row.completion_status)
          : "";
    const approvalRaw =
      row.approval_status != null ? String(row.approval_status).trim().toLowerCase() : "";
    const statusKey =
      approvalRaw === "awaiting_review" ||
      approvalRaw === "pending_review" ||
      approvalRaw === "in_review"
        ? "needs_review"
        : approvalRaw === "approved" || approvalRaw === "conditionally_approved"
          ? "complete"
          : statusRaw.trim().toLowerCase();
    const createdAt =
      typeof row.created_at === "number"
        ? row.created_at
        : Number(row.created_at) || 0;
    paired.push({
      createdAt,
      group: {
        sessionId: sessionId || "—",
        status:
          statusKey === "needs_review"
            ? "Needs Review"
            : statusKey === "complete"
              ? "Complete"
              : statusRaw
                ? formatStatusLabel(statusRaw)
                : "—",
        statusKey,
        completionStatus:
          row.completion_status != null ? String(row.completion_status) : null,
        approvalStatus:
          row.approval_status != null ? String(row.approval_status) : null,
        idDocumentType:
          row.id_document_type != null ? String(row.id_document_type) : null,
        verificationChecks,
        createdAtLabel: formatAtLabel(row.created_at),
        eventRows,
      },
    });
  }
  paired.sort((a, b) => b.createdAt - a.createdAt);
  return paired.map((p) => p.group);
}

export function hasAnyVerifastEventRows(groups: VerifastSessionGroup[]): boolean {
  return groups.some((g) => g.eventRows.length > 0);
}
